import { isRecord, finiteNumber } from "../service-json";
import type { VideoSubject } from "./video-types";

/**
 * Le cadrage d'une caméra : ce que l'interprète décide qu'elle montre.
 *
 * # Ce n'est pas un réglage d'affichage
 *
 * Rien ici ne se pose en CSS chez qui regarde. Le cadrage voyage jusqu'à
 * l'appareil qui filme, et c'est lui qui rogne **avant son encodeur** : ce
 * qu'on écarte du champ ne sort jamais de l'appareil — ni vers la salle, ni
 * vers le SFU, ni vers l'enregistrement, donc ni vers les fichiers du replay,
 * qui sont servis à qui a le lien.
 *
 * Un cadrage appliqué à l'affichage aurait laissé l'image entière chez chaque
 * spectateur : un `transform` retiré dans l'inspecteur, et le champ écarté
 * réapparaît. Recadrer pour masquer quelque chose n'aurait alors rien masqué
 * du tout. Voir `docs/spec/video-engine.md`.
 *
 * # Pourquoi il passe par l'état de séance
 *
 * Parce que c'est le seul canal qui atteigne un appareil appairé : son jeton ne
 * souscrit à rien et ne publie pas de données, il ne lit que la métadonnée de
 * la salle — c'est déjà par là que le rideau lui coupe sa caméra
 * (`livekit-paired-device.ts`). Et parce qu'un cadrage est un fait de séance :
 * l'appareil qui se reconnecte le retrouve sans que personne ait à le lui
 * renvoyer.
 */
export interface CameraFraming {
  /** Rotation de l'image, en degrés dans le sens des aiguilles. */
  readonly rotation: CameraRotation;
  /**
   * Zoom, où 1 est ce que l'objectif donne sans qu'on lui demande rien.
   *
   * Sous 1, c'est un grand angle : l'appareil n'y arrive que s'il en a un —
   * le zoom 0,5 de la caméra virtuelle sous iOS, un objectif à part ailleurs.
   * Au-dessus, il zoome optiquement s'il le peut et rogne sinon. Le cadrage dit
   * le champ voulu ; lequel des deux chemins le donne ne se décide que sur
   * l'appareil.
   */
  readonly zoom: number;
  /** Centre du cadrage, en fractions de l'image depuis son milieu : ±1 est le bord. */
  readonly x: number;
  readonly y: number;
  /**
   * Ce que la caméra filme. `null` : des mains, ce que filme une caméra dont
   * personne n'a rien dit (voir {@link subjectIn}).
   *
   * En direct, il décide de la retenue des images — des mains tombent avec les
   * notes, un visage suit le calage de la parole —, donc en changer fait un
   * saut dans l'image. C'est le comportement juste, et il se voit. Au replay, il
   * décide de la place de l'image : des mains au pied du rouleau, un visage en
   * bulle.
   */
  readonly subject: VideoSubject | null;
  /** L'objectif à ouvrir, tel que le navigateur de l'appareil le nomme ; `null` : le sien. */
  readonly lens: string | null;
}

/**
 * Une rotation, en degrés entiers dans le sens des aiguilles : 0 à 359.
 *
 * # Un seul angle, deux gestes dedans
 *
 * L'algorithme le décompose en **quart de tour** — l'orientation de l'appareil,
 * qui ne coûte rien : les côtés s'échangent et toute l'image reste — et en
 * **redressement**, ce qui dépasse du quart de tour le plus proche. Seul le
 * redressement coûte un zoom, parce qu'une image inclinée ne remplit plus un
 * cadre droit ; et ce zoom se calcule, il ne se demande pas.
 *
 * Sans cette décomposition, un téléphone posé sur le flanc aurait payé 1,78×
 * de zoom pour un quart de tour qui ne perd rien.
 */
export type CameraRotation = number;

/** Ramène un angle dans le tour, en degrés entiers : c'est ce que le service accepte. */
export function normaliseRotation(degrees: number): CameraRotation {
  return ((Math.round(degrees) % 360) + 360) % 360;
}

/** Le quart de tour le plus proche, en nombre de quarts (0 à 3), et ce qui dépasse. */
export function splitRotation(rotation: number): { quarters: number; tiltDeg: number } {
  const turns = Math.round(rotation / 90);

  return { quarters: ((turns % 4) + 4) % 4, tiltDeg: rotation - turns * 90 };
}

/**
 * Le zoom qu'il faut pour qu'une image inclinée de `tiltDeg` remplisse encore
 * un cadre de mêmes proportions.
 *
 * # D'où il sort
 *
 * Le cadre visible, de demi-côtés (W/2, H/2), tourné de l'angle dans le repère
 * de l'image, déborde selon ses deux diagonales : il tient si
 * `W·c + H·s ≤ w` et `W·s + H·c ≤ h`, avec `c` et `s` les valeurs absolues du
 * cosinus et du sinus. À proportions gardées (`W/H = w/h`), les deux donnent
 * chacun un zoom, et c'est le plus grand qui commande.
 *
 * Un carré incliné de 45° demande √2 ; un angle nul ne demande rien.
 */
export function fillZoom(tiltDeg: number, width: number, height: number): number {
  if (tiltDeg === 0 || width <= 0 || height <= 0) {
    return 1;
  }

  const radians = (tiltDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  return Math.max((width * c + height * sin) / width, (width * sin + height * c) / height);
}

/** Les bornes du zoom, les mêmes que celles que le service refuse (`session.Framing`). */
export const MIN_CAMERA_ZOOM = 0.5;
export const MAX_CAMERA_ZOOM = 5;

/** Le cadrage d'une caméra dont personne n'a rien décidé. */
export const DEFAULT_FRAMING: CameraFraming = {
  rotation: 0,
  zoom: 1,
  x: 0,
  y: 0,
  subject: null,
  lens: null,
};

/**
 * Deux cadrages qui demandent la même chose.
 *
 * Par valeur et non par identité : la table est relue à chaque publication de
 * métadonnée — une position, une audience —, donc les cadrages sont des objets
 * neufs bien plus souvent qu'ils ne changent.
 */
export function sameFraming(a: CameraFraming, b: CameraFraming): boolean {
  return (
    a.rotation === b.rotation &&
    a.zoom === b.zoom &&
    a.x === b.x &&
    a.y === b.y &&
    a.subject === b.subject &&
    a.lens === b.lens
  );
}

/**
 * Une clé de valeur : deux cadrages qui demandent la même chose ont la même.
 * Pour ce qui doit se réarmer sur un cadrage qui **change**, et non sur un
 * objet neuf — la table est relue à chaque publication de métadonnée.
 */
export function framingKey(framing: CameraFraming): string {
  return `${framing.rotation}|${framing.zoom}|${framing.x}|${framing.y}|${framing.subject ?? ""}|${framing.lens ?? ""}`;
}

/** Ce cadrage ne demande rien : l'image telle que l'appareil la donne. */
export function isDefaultFraming(framing: CameraFraming): boolean {
  return (
    framing.rotation === 0 &&
    framing.zoom === 1 &&
    framing.x === 0 &&
    framing.y === 0 &&
    framing.subject === null &&
    framing.lens === null
  );
}

/**
 * Les cadrages publiés dans l'état de séance, par identité LiveKit de qui
 * publie la caméra.
 *
 * L'identité du publieur, et non le `sid` de la piste : une piste republiée —
 * une reconnexion, un rideau relevé, un téléphone revenu de l'arrière-plan —
 * en change, et le cadrage doit lui survivre.
 */
export type CameraFramings = Readonly<Record<string, CameraFraming>>;

/** Ce que filme une caméra dont personne n'a rien dit. */
export const DEFAULT_SUBJECT: VideoSubject = "hands";

/**
 * Ce que filme la caméra de cette identité, selon la table de la séance.
 *
 * # Le seul endroit où ça se dit
 *
 * Ni le nom de sa piste ni son lien d'appairage n'en portent rien. Le nom de
 * la piste l'a porté, et c'était un second endroit pour le même fait : il se
 * fixait à la publication, pendant que la table le changeait d'un geste — et
 * le 2026-09-21, la retenue des images se calculait encore sur le nom quand la
 * liste des sources montrait déjà la table. Il ne reste que la table. Le
 * direct la lit dans la métadonnée de la salle, le replay dans le journal des
 * états que l'enregistreur en a gardé.
 *
 * Une caméra dont elle ne dit rien filme des mains : un téléphone posé
 * au-dessus du clavier, la webcam d'un récital.
 */
export function subjectIn(cameras: CameraFramings, identity: string): VideoSubject {
  return cameras[identity]?.subject ?? DEFAULT_SUBJECT;
}

/**
 * Lit la table publiée. Tolérante comme le reste de la charge : une entrée
 * qu'on ne sait pas lire est écartée, jamais la table entière — un cadrage
 * fautif ne vaut pas de perdre la séance.
 */
export function parseCameraFramings(raw: unknown): CameraFramings {
  if (!isRecord(raw)) {
    return {};
  }

  const out: Record<string, CameraFraming> = {};

  for (const [identity, value] of Object.entries(raw)) {
    const framing = parseFraming(value);

    if (identity !== "" && framing !== null) {
      out[identity] = framing;
    }
  }

  return out;
}

function parseFraming(raw: unknown): CameraFraming | null {
  if (!isRecord(raw)) {
    return null;
  }

  const framing: CameraFraming = {
    rotation: rotationOf(raw.rotation),
    // Zéro vaut 1 : c'est ce que porte une charge dont le champ est absent, et
    // c'est ce que le service normalise de son côté.
    zoom: zoomOf(raw.zoom),
    x: offsetOf(raw.x),
    y: offsetOf(raw.y),
    subject: subjectOf(raw.subject),
    lens: typeof raw.lens === "string" && raw.lens !== "" ? raw.lens : null,
  };

  return isDefaultFraming(framing) ? null : framing;
}

/** Un angle entier du tour, ou zéro. Les mêmes bornes que celles du service. */
function rotationOf(raw: unknown): CameraRotation {
  const rotation = finiteNumber(raw);

  if (rotation === null || rotation < 0 || rotation > 359) {
    return 0;
  }

  return Math.round(rotation);
}

function zoomOf(raw: unknown): number {
  const zoom = finiteNumber(raw);

  if (zoom === null || zoom < MIN_CAMERA_ZOOM || zoom > MAX_CAMERA_ZOOM) {
    return 1;
  }

  return zoom;
}

function offsetOf(raw: unknown): number {
  const offset = finiteNumber(raw);

  if (offset === null || offset < -1 || offset > 1) {
    return 0;
  }

  return offset;
}

function subjectOf(raw: unknown): VideoSubject | null {
  return raw === "hands" || raw === "face" ? raw : null;
}

/** Les dimensions d'une image. */
export interface FrameSize {
  readonly width: number;
  readonly height: number;
}

/** L'étendue du zoom d'une piste, quand le navigateur la donne. */
export interface OpticalZoomRange {
  readonly min: number;
  readonly max: number;
}

/**
 * Ce que la toile dessine : l'image de la source, tournée de `angle` autour du
 * point `(cx, cy)` de cette image, ramené au centre de la toile.
 *
 * Aucune mise à l'échelle : c'est la **taille de la toile** qui fait le zoom —
 * une fenêtre plus petite dans les mêmes pixels. Zoomer réduit donc la
 * définition publiée au lieu d'agrandir des pixels, et l'encodeur reçoit moins
 * d'images à coder plutôt que des images grossies.
 */
export interface FramingDraw {
  /** L'angle total, en radians, dans le sens des aiguilles. */
  readonly angle: number;
  /** Le point de l'image de la source qui se retrouve au centre de la toile. */
  readonly cx: number;
  readonly cy: number;
  /**
   * De combien réduire l'image en la dessinant. 1 la plupart du temps : c'est
   * la taille de la toile qui fait le zoom.
   *
   * Sous 1 quand la découpe dépasse le plafond de publication : on capte plus
   * large qu'on ne publie, précisément pour avoir des pixels à rogner, et
   * l'excédent se réduit ici au lieu de partir dans l'encodeur. Voir
   * {@link MAX_OUTPUT_PIXELS}.
   */
  readonly scale: number;
}

/**
 * Ce qu'on publie au plus, en pixels par image.
 *
 * # Pourquoi un compte de pixels et pas une définition
 *
 * Parce que le format varie : une caméra couchée publie du 1280×720, la même
 * debout du 720×1280, et un jour peut-être un carré. Une borne par côté aurait
 * traité ces trois cas comme trois réglages ; un compte de pixels dit la seule
 * chose qui compte vraiment — ce que la couche haute coûte à encoder et à
 * transmettre — et vaut dans toutes les orientations.
 *
 * C'est exactement 1280×720, la couche haute de `cameraLayers`.
 *
 * # À quoi il sert
 *
 * À découpler la **capture** de la **publication**. La capture vise plus haut
 * que le plafond (voir `CAMERA_CAPTURE`) pour qu'il reste des pixels à rogner :
 * sans lui, zoomer 2× dans du 720p ne publiait que 640×360, et l'image étalée
 * sur tout le rouleau était molle. Avec lui, on capte en 1080p, on rogne dedans,
 * et on publie toujours au plus 720p — même débit qu'avant, plus de détail.
 */
export const MAX_OUTPUT_PIXELS = 1280 * 720;

/**
 * Ce qu'il faut faire de la caméra pour obtenir le cadrage demandé.
 *
 * Deux chemins, et c'est l'appareil qui décide lequel : le zoom **optique**
 * quand la piste en a un — l'entourage n'est alors jamais capté, et l'image ne
 * perd pas un pixel —, la **découpe** sinon. Le cadrage dit le champ voulu, pas
 * la manière.
 */
export interface FramingPlan {
  /** Le zoom à poser sur la piste ; `null` quand elle n'en a pas. */
  readonly opticalZoom: number | null;
  /**
   * Ce qu'il y a à dessiner. `null` seulement quand la source ne dit pas ses
   * dimensions, auquel cas on ne saurait pas où découper.
   */
  readonly draw: FramingDraw | null;
  /**
   * Le zoom effectivement appliqué par la découpe, redressement compris : ce
   * que le demandé est devenu une fois l'auto-zoom ajouté.
   */
  readonly digitalZoom: number;
  /** De combien le centre peut se déplacer, en pixels de la source, sur chaque axe. */
  readonly marginX: number;
  readonly marginY: number;
  readonly rotation: CameraRotation;
  /** L'image publiée, une fois rognée et tournée. */
  readonly output: FrameSize;
  /**
   * La piste de la caméra se publie telle quelle : rien à rogner, rien à
   * tourner.
   *
   * C'est le cas courant, et il ne doit rien coûter : sans lui, chaque
   * téléphone de chaque récital ferait tourner une boucle de dessin à 30
   * images par seconde pour recopier son image à l'identique.
   */
  readonly passthrough: boolean;
}

/**
 * Ce que l'appareil doit faire pour filmer ce cadrage, dans l'image qu'il
 * capte aujourd'hui.
 *
 * # Le zoom de la piste ne sert qu'au grand angle
 *
 * Au-dessus de 1, c'est la découpe qui zoome, même quand la piste saurait le
 * faire elle-même. Parce que **déplacer le cadre, c'est montrer une autre
 * partie de ce que la caméra voit** : il faut donc qu'elle filme plus large
 * qu'elle ne montre, et un zoom pris par l'optique ne laisse aucune marge —
 * la caméra cadre exactement ce qu'elle publie, et les deux curseurs de centre
 * ne commandent plus rien.
 *
 * Sous 1, l'inverse : aucune découpe n'élargit une image, et seul un objectif
 * peut donner le champ. Le zoom de la piste y est le seul chemin — le 0,5× de
 * la caméra virtuelle sous iOS, un objectif à part ailleurs.
 *
 * Une piste qui sait zoomer est donc **remise à 1** dès qu'on ne lui demande
 * plus le grand angle : sans ça, un zoom posé plus tôt se multiplierait avec la
 * découpe.
 *
 * # Ce qui ne se rattrape pas
 *
 * Un grand angle qu'aucune optique ne donne. On ne peut pas élargir une image
 * en la rognant : un zoom sous 1 sans optique pour le porter rend le champ
 * ordinaire, et rien de plus. Mieux vaut ça qu'une image étirée.
 *
 * # Pourquoi des dimensions paires
 *
 * Les encodeurs H.264 travaillent par blocs et refusent — ou rabotent en
 * silence — une dimension impaire. Une découpe arrondie au pixel près donnerait
 * une image d'un pixel de trop une fois sur deux, à chaque changement de zoom.
 */
export function planFraming(
  source: FrameSize,
  framing: CameraFraming,
  zoom: OpticalZoomRange | null = null,
): FramingPlan {
  // Le grand angle seul passe par la piste ; tout le reste vaut 1, ce qui la
  // remet dans son champ ordinaire.
  const optical = zoom === null ? null : clamp(Math.min(framing.zoom, 1), zoom.min, zoom.max);

  // Une source dont on ne connaît pas les dimensions ne se découpe pas : on ne
  // saurait pas où. Elle se publie telle quelle plutôt que de partir sur une
  // toile vide — le zoom optique, lui, a déjà été calculé et part quand même.
  if (source.width <= 0 || source.height <= 0) {
    return {
      opticalZoom: optical,
      draw: null,
      digitalZoom: 1,
      marginX: 0,
      marginY: 0,
      rotation: framing.rotation,
      output: source,
      passthrough: true,
    };
  }

  const { quarters, tiltDeg } = splitRotation(framing.rotation);
  const upright = quarters % 2 === 0;

  // Les côtés après le quart de tour : c'est le cadre que le redressement doit
  // remplir, et celui que la toile aura.
  const ow = upright ? source.width : source.height;
  const oh = upright ? source.height : source.width;

  // Ce que l'optique n'a pas donné, la découpe le prend — jamais sous 1 : une
  // découpe n'élargit rien. Puis le redressement s'y ajoute, calculé.
  const asked = Math.max(1, framing.zoom / (optical ?? 1));
  const digital = asked * fillZoom(tiltDeg, ow, oh);

  // Le cadre visible, en pixels de la source. C'est lui qui décide des marges,
  // quoi qu'on publie ensuite.
  const seenW = ow / digital;
  const seenH = oh / digital;

  // Et ce qu'on publie de ce cadre : tout, ou le plafond. Réduire ici plutôt
  // que d'encoder une image plus grande, c'est garder le débit d'avant en
  // gagnant du détail — l'image réduite d'une capture large est plus fine que
  // la même découpe prise dans une capture étroite.
  const scale = Math.min(1, Math.sqrt(MAX_OUTPUT_PIXELS / (seenW * seenH)));
  const width = even(seenW * scale);
  const height = even(seenH * scale);

  // Le cadre visible, ramené dans le repère de la source : c'est son
  // encombrement qui dit jusqu'où le centre peut aller.
  const angle = (framing.rotation * Math.PI) / 180;
  const c = Math.abs(Math.cos(angle));
  const sin = Math.abs(Math.sin(angle));
  const marginX = Math.max(0, (source.width - (seenW * c + seenH * sin)) / 2);
  const marginY = Math.max(0, (source.height - (seenW * sin + seenH * c)) / 2);

  return {
    opticalZoom: optical,
    draw: {
      angle,
      cx: source.width / 2 + clamp(framing.x, -1, 1) * marginX,
      cy: source.height / 2 + clamp(framing.y, -1, 1) * marginY,
      scale,
    },
    digitalZoom: digital,
    marginX,
    marginY,
    rotation: framing.rotation,
    output: { width, height },
    // Rien à dessiner : l'image entière, dans le bon sens, et qui tient sous
    // le plafond. C'est le cas courant, et c'est lui qui doit ne rien coûter.
    passthrough: framing.rotation === 0 && digital === 1 && scale === 1,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Au pair **inférieur**, et jamais zéro : une image a au moins deux pixels.
 *
 * Vers le bas et non au plus proche : le cadre publié est ainsi toujours un
 * peu plus serré que le calcul, jamais plus large. Arrondi au plus proche, il
 * arrivait qu'un redressement laisse un cheveu de vide dans un coin — le zoom
 * de remplissage tombait un dix-millième sous ce qu'il fallait.
 */
function even(value: number): number {
  return Math.max(2, Math.floor(value / 2) * 2);
}

/**
 * Les dimensions qu'avait la source, retrouvées depuis l'image publiée.
 *
 * **Un repli, et plus la réponse.** Depuis que l'appareil se déclare au
 * service (`camera-capabilities.ts`), le studio connaît les vraies dimensions
 * et n'a rien à deviner. Cette fonction sert quand la déclaration manque : un
 * service redémarré, un appareil qui n'a pas pu se déclarer.
 *
 * Et elle ne peut plus être exacte dans tous les cas : dès que la découpe
 * dépasse {@link MAX_OUTPUT_PIXELS}, la définition publiée est plafonnée, et
 * le plafond détruit précisément ce qu'on remonte ici — toutes les découpes
 * plus grandes que le plafond publient la même chose. Elle rend alors une
 * source trop petite, et le geste est trop sensible. C'est un mode dégradé
 * assumé, pas le chemin normal.
 *
 * # Pourquoi il faut les deviner
 *
 * Parce que l'écran qui commande le cadrage ne les connaît pas : d'un
 * téléphone, il ne reçoit que ce que l'appareil a déjà rogné — c'est le sens
 * même du rognage à la source. Et il en a besoin pour savoir de combien le
 * centre peut encore se déplacer : sans marge connue, un glissement sur
 * l'image ne saurait pas quelle fraction il parcourt.
 *
 * # Comment
 *
 * L'image publiée est la source orientée, divisée par le zoom total. Le zoom
 * du redressement ne dépend que des **proportions** du cadre, et celles-ci
 * survivent à la division : elles se lisent donc directement sur l'image
 * publiée. Le reste se remonte.
 *
 * À deux pixels près, que l'arrondi aux dimensions paires a mangés.
 */
export function sourceFromOutput(output: FrameSize, framing: CameraFraming): FrameSize {
  if (output.width <= 0 || output.height <= 0) {
    return output;
  }

  const { quarters, tiltDeg } = splitRotation(framing.rotation);
  const total = Math.max(1, framing.zoom) * fillZoom(tiltDeg, output.width, output.height);
  const ow = output.width * total;
  const oh = output.height * total;

  return quarters % 2 === 0 ? { width: ow, height: oh } : { width: oh, height: ow };
}

/**
 * La transformation CSS qui montre le cadrage `draft` à partir de l'image
 * publiée sous le cadrage `published`.
 *
 * # Pourquoi le geste se rend ici, et pas en repassant par l'appareil
 *
 * Parce qu'un cadrage qui traverse le service, la métadonnée de salle, le
 * réseau jusqu'à l'appareil, sa toile, son encodeur et le chemin du retour ne
 * peut pas suivre un doigt. Le geste se dessine donc sur place, sur les pixels
 * déjà reçus, et les vraies données partent amorties derrière. Quand elles
 * arrivent, l'image est déjà à sa place et la transformation redevient
 * l'identité d'elle-même — sans à-coup, puisqu'elle est calculée **entre les
 * deux cadrages** et non depuis le début du geste.
 *
 * # Ce qu'elle ne peut pas faire
 *
 * Inventer ce qu'on n'a pas reçu. Élargir ou déplacer vers ce que l'appareil a
 * coupé découvre du vide — c'est le prix du rognage à la source, et c'est
 * volontaire : ces pixels-là ne sont jamais sortis de l'appareil. Le vide se
 * remplit quand le cadrage atteint la caméra.
 *
 * Rend une chaîne vide quand il n'y a rien à montrer, ou quand les deux
 * cadrages ne regardent pas dans le même sens : un quart de tour échange les
 * côtés de l'image, ce qu'aucune translation ne rattrape. Ce n'est pas un
 * geste, c'est un bouton.
 */
export function framingTransform(published: CameraFraming, draft: CameraFraming, source: FrameSize): string {
  const from = planFraming(source, published);
  const to = planFraming(source, draft);

  if (from.draw === null || to.draw === null || to.output.width === 0 || to.output.height === 0) {
    return "";
  }

  if (splitRotation(published.rotation).quarters % 2 !== splitRotation(draft.rotation).quarters % 2) {
    return "";
  }

  // Le point que l'image publiée montre en son centre, vu depuis le cadre
  // qu'on vise : c'est de là que vient la translation. En pixels de la source.
  const angle = (draft.rotation * Math.PI) / 180;
  const dx = from.draw.cx - to.draw.cx;
  const dy = from.draw.cy - to.draw.cy;
  const tx = dx * Math.cos(angle) - dy * Math.sin(angle);
  const ty = dx * Math.sin(angle) + dy * Math.cos(angle);

  // En pourcentages de l'élément : il n'y a alors rien à mesurer à l'écran, et
  // la transformation vaut quelle que soit la taille à laquelle on montre.
  //
  // `draw.scale` fait le passage des pixels de la source à ceux de l'image
  // publiée : les deux ne sont plus les mêmes depuis que la découpe est
  // plafonnée.
  const px = round((100 * tx * to.draw.scale) / to.output.width);
  const py = round((100 * ty * to.draw.scale) / to.output.height);

  // Le grossissement, c'est le rapport des champs **vus**, pas des définitions
  // publiées : une image plafonnée montre le même champ dans moins de pixels.
  const scale = round(seenSize(from).width / seenSize(to).width);
  const turn = round(draft.rotation - published.rotation);

  return `translate(${px}%, ${py}%) rotate(${turn}deg) scale(${scale})`;
}

/**
 * Où poser l'image **entière** d'une caméra pour y voir un cadrage : la taille,
 * l'origine et la transformation d'un élément qui montre la source telle
 * qu'elle filme, dans une vignette au format de l'image cadrée.
 *
 * # À qui elle sert
 *
 * À la webcam de ce poste, et à elle seule. Son image entière est déjà ici — la
 * caméra est branchée sur cette machine — et cadrer dessus ne fait rien
 * sortir : le geste se dessine sur la vraie image, et seul le cadrage validé
 * part à l'appareil qui rogne. D'un appareil appairé on ne reçoit que ce qu'il
 * a déjà rogné, et c'est {@link framingTransform} qui s'en occupe, avec ses
 * limites.
 *
 * # Les mêmes calculs que la toile
 *
 * L'élément est la source à l'échelle de la vignette, tourné autour du point
 * `(cx, cy)` de la source — c'est l'origine de la transformation —, et ce point
 * est ramené au centre de la vignette. C'est mot pour mot ce que dessine
 * `FramedCamera.paint` : ce que l'aperçu montre est ce que la toile publiera,
 * au pixel pair près. Un quart de tour s'y dessine comme le reste, puisque
 * l'élément n'a pas à remplir la vignette : c'est la vignette qui prend le
 * format du cadrage.
 *
 * Tout est en fractions : rien à mesurer à l'écran, et la vignette peut changer
 * de taille sous l'aperçu sans que rien ne se recalcule. `null` quand la source
 * ne dit pas ses dimensions — on ne saurait pas où poser l'image.
 */
export interface FramingView {
  /** Le format de la vignette : largeur sur hauteur de l'image cadrée. */
  readonly aspect: number;
  /** La taille de l'élément, en fractions de la vignette : plus grand qu'elle dès qu'on zoome. */
  readonly width: number;
  readonly height: number;
  /** L'origine de la rotation, en fractions de l'élément : le point de la source qui va au centre. */
  readonly originX: number;
  readonly originY: number;
  /** De combien déplacer l'élément pour que l'origine tombe au centre, en fractions de l'élément. */
  readonly dx: number;
  readonly dy: number;
  /** L'angle, en degrés dans le sens des aiguilles. */
  readonly rotation: CameraRotation;
}

export function framingView(source: FrameSize, framing: CameraFraming): FramingView | null {
  const plan = planFraming(source, framing);

  if (plan.draw === null) {
    return null;
  }

  const { width: ow, height: oh } = plan.output;

  return {
    aspect: ow / oh,
    width: source.width / ow,
    height: source.height / oh,
    originX: plan.draw.cx / source.width,
    originY: plan.draw.cy / source.height,
    dx: (ow / 2 - plan.draw.cx) / source.width,
    dy: (oh / 2 - plan.draw.cy) / source.height,
    rotation: framing.rotation,
  };
}

/**
 * La feuille de style d'un {@link FramingView}, à poser sur l'élément.
 *
 * `translate` puis `rotate`, dans cet ordre : la rotation se fait autour de
 * l'origine, et c'est l'origine qu'on déplace ensuite jusqu'au centre — l'ordre
 * inverse tournerait autour d'un point déjà parti.
 */
export function framingViewStyle(view: FramingView): {
  width: string;
  height: string;
  transformOrigin: string;
  transform: string;
} {
  return {
    width: `${round(100 * view.width)}%`,
    height: `${round(100 * view.height)}%`,
    transformOrigin: `${round(100 * view.originX)}% ${round(100 * view.originY)}%`,
    transform: `translate(${round(100 * view.dx)}%, ${round(100 * view.dy)}%) rotate(${round(view.rotation)}deg)`,
  };
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Le champ vu, en pixels **de la source**.
 *
 * Ce n'est plus la définition publiée depuis que la découpe est plafonnée : une
 * image plafonnée montre le même champ dans moins de pixels. Tout ce qui
 * convertit un geste de l'écran vers la source passe par là — sans quoi le
 * geste court à côté dès que le plafond mord.
 */
export function seenSize(plan: FramingPlan): FrameSize {
  if (plan.draw === null || plan.draw.scale === 0) {
    return plan.output;
  }

  return { width: plan.output.width / plan.draw.scale, height: plan.output.height / plan.draw.scale };
}
